import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faFile,
  faFileAudio,
  faFileBinary,
  faFileCertificate,
  faFileCode,
  faFileCsv,
  faFileExcel,
  faFileImage,
  faFileLines,
  faFilePdf,
  faFileVideo,
  faFileWord,
  faFileZipper,
} from '@fortawesome/pro-solid-svg-icons';

/**
 * Picks the icon for a support-ticket attachment.
 *
 * Keyed on the FILE EXTENSION rather than the reported content type, because for the files that
 * actually turn up on game-server tickets the content type is close to useless: browsers send
 * `application/octet-stream` for `.cfg`, `.log`, `.properties`, `.sav` and most of the rest, so
 * content-type matching would land nearly everything on the generic icon.
 *
 * The content type is still consulted as a fallback for the three categories where browsers are
 * reliable and the extension list can never be complete — images, video and audio.
 *
 * Anything unrecognised gets the plain file icon. That is a normal outcome, not a gap to be
 * hunted: the list below covers what support actually receives, and a long tail will always exist.
 */

// Extensions are grouped by the icon they map to, so adding one is a single-word edit and the
// grouping itself documents why (`.jar` is a zip; `.sav` is opaque binary).
const ICONS_BY_EXTENSION: ReadonlyArray<
  readonly [IconDefinition, readonly string[]]
> = [
  // Logs and plain text — by a wide margin the most common thing a customer attaches.
  [faFileLines, ['log', 'txt', 'out', 'err', 'md', 'nfo']],

  // Server configuration. The reason this helper keys on extension: every one of these arrives as
  // application/octet-stream or text/plain.
  [
    faFileCode,
    [
      'cfg',
      'conf',
      'config',
      'ini',
      'properties',
      'toml',
      'yml',
      'yaml',
      'json',
      'xml',
      'lua',
      'sh',
      'bat',
      'cmd',
      'ps1',
      'py',
      'js',
      'ts',
      'sql',
      'html',
      'css',
      'vdf',
      'acf',
    ],
  ],

  // Archives. `.jar` and the Minecraft bundles are zips underneath, and customers send them as
  // mods, plugins and world backups.
  [
    faFileZipper,
    [
      'zip',
      'rar',
      '7z',
      'tar',
      'gz',
      'tgz',
      'bz2',
      'xz',
      'jar',
      'pak',
      'mcworld',
      'mcpack',
      'mrpack',
    ],
  ],

  // Opaque binaries: world saves, databases, dumps. Distinguishing these from an archive matters —
  // an agent should not expect to open one.
  [
    faFileBinary,
    [
      'sav',
      'dat',
      'db',
      'sqlite',
      'sqlite3',
      'bin',
      'dmp',
      'mdb',
      'exe',
      'dll',
    ],
  ],

  [faFileImage, ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif']],
  [faFileVideo, ['mp4', 'mov', 'webm', 'avi', 'mkv', 'flv']],
  [faFileAudio, ['mp3', 'wav', 'ogg', 'flac', 'm4a']],

  [faFilePdf, ['pdf']],
  [faFileCsv, ['csv', 'tsv']],
  [faFileExcel, ['xls', 'xlsx', 'ods']],
  [faFileWord, ['doc', 'docx', 'odt', 'rtf']],

  // Certificates and keys turn up on tickets about custom domains and RCON/TLS setup.
  [faFileCertificate, ['pem', 'crt', 'cer', 'key', 'p12', 'pfx']],
];

const ICON_BY_EXTENSION: ReadonlyMap<string, IconDefinition> = new Map(
  ICONS_BY_EXTENSION.flatMap(([icon, extensions]) =>
    extensions.map((extension) => [extension, icon] as const),
  ),
);

const extensionOf = (name: string): string => {
  // lastIndexOf, so `world.backup.tar.gz` yields `gz` rather than `backup.tar.gz`. A leading dot is
  // not an extension: `.gitignore` has none.
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return '';

  return name.slice(dot + 1).toLowerCase();
};

/**
 * Whether an attachment can be shown as an image rather than offered as a download.
 *
 * Content type FIRST here, which is the opposite of `attachmentIcon` below, and deliberately so.
 * The icon has to work for files browsers describe as `application/octet-stream`, where only the
 * extension carries information. Rendering is the other way round: putting a URL in an `<img>` is a
 * claim that the bytes really are an image, and the server-sent content type is the better evidence
 * for that than a name a customer can call anything.
 *
 * SVG is excluded on purpose. It is an image to a browser and a script host to an attacker, and
 * these files come from customers; offering it as a download is the safe half of the behaviour.
 */
const INLINE_IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'bmp',
  'avif',
]);

/**
 * The bare, lowercased media type: `image/svg+xml; charset=utf-8` -> `image/svg+xml`.
 *
 * Both halves matter and both were wrong before. A parameter after the semicolon made the SVG
 * comparison below fail, so `image/svg+xml; charset=utf-8` — which is what a server that labels its
 * SVGs properly sends — went into an `<img>`, the one case this must never allow. And an uppercase
 * `IMAGE/PNG` failed the `image/` prefix test and then got rejected outright, so a real image
 * rendered as a download link. RFC 9110 makes type and subtype case-insensitive and parameters
 * optional, so neither input is exotic.
 */
const mediaType = (contentType: string): string =>
  contentType.split(';')[0].trim().toLowerCase();

export const isInlineImage = (name: string, contentType = ''): boolean => {
  const type = mediaType(contentType);

  if (type.startsWith('image/')) {
    return type !== 'image/svg+xml';
  }

  // A specific non-image content type is authoritative. Falling through to the extension here would
  // put an `application/zip` called `screenshot.png` into an `<img>` and render a broken one.
  if (type && type !== 'application/octet-stream') {
    return false;
  }

  // Nothing usable said — the generic type, or none at all. The extension is all that is left.
  return INLINE_IMAGE_EXTENSIONS.has(extensionOf(name));
};

export const attachmentIcon = (
  name: string,
  contentType = '',
): IconDefinition => {
  const byExtension = ICON_BY_EXTENSION.get(extensionOf(name));
  if (byExtension) return byExtension;

  // Only for the categories where the browser's content type is trustworthy. A `.log` arriving as
  // text/plain must NOT land here — it is already handled above, and falling through to a text
  // icon for every octet-stream would defeat the point.
  if (contentType.startsWith('image/')) return faFileImage;
  if (contentType.startsWith('video/')) return faFileVideo;
  if (contentType.startsWith('audio/')) return faFileAudio;

  return faFile;
};
